import React, { useState, useEffect, useRef } from "react";
import { Badge, Persona, Toast } from "@fluentui/react-components";
import {
  FluentProvider,
  webLightTheme,
  webDarkTheme,
} from "@fluentui/react-components";
import {
  FiMoreVertical,
  FiHeadphones,
  FiMic,
  FiArrowUpCircle,
  FiArrowDownCircle,
  FiSearch,
} from "react-icons/fi";
import AgentStatus from "./Timer";
import { BiSortAZ } from "react-icons/bi";
import { MdOutlineEventAvailable } from "react-icons/md";
import { EuiLoadingSpinner } from "@elastic/eui";
import { useTheme } from "../../context/ThemeContext";

const presenceOrder = {
  inacall: 1,
  busy: 2,
  available: 3,
  berightback: 4,
  away: 4,
  offline: 5,
  offwork: 5,
  presenceunknown: 6,
};

const RealtimeAgents = ({ agents, loading = true }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortType, setSortType] = useState("availability");

  const { isDarkMode } = useTheme();

  const capitalize = (str) => {
    if (!str) return "Unknown";

    const words = str
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2");

    return words
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Filtro y ordenamiento
  const filteredAndSortedAgents = [...agents]
    .filter((agent) =>
      agent.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortType === "alphabetical") {
        return a.name.localeCompare(b.name);
      } else if (sortType === "availability") {
        const aActivity = a.presenceActivity?.toLowerCase() || "offline";
        const bActivity = b.presenceActivity?.toLowerCase() || "offline";
        return (
          presenceOrder[aActivity] - presenceOrder[bActivity] ||
          a.name.localeCompare(b.name)
        );
      }
      return 0;
    });

  if (agents.length === 0 && !loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4 text-center">
        <h3 className="text-xl font-bold text-gray-800">
          No Hay Agentes Disponibles
        </h3>
      </div>
    );
  }

  return (
    <div className="group h-full overflow-visible w-full p-2">
      {/* Search and Sorting Controls */}
      <div className="mb-4 bg-primary-light dark:bg-primary-dark rounded-lg shadow-md sticky top-0 z-10 flex items-center p-1 w-full gap-2">
        {/* Search Bar */}
        <div className="flex items-center rounded-xl w-full px-2">
          <FiSearch className="text-gray-500 mr-2" />
          <input
            type="text"
            placeholder="Search by agent"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="outline-none text-md py-2 px w-full placeholder-gray-400 dark:bg-primary-dark dark:text-gray-200"
          />
        </div>

        {/* Sorting Buttons */}
        <div className="flex gap-1 items-center">
          <button
            onClick={() => setSortType("alphabetical")}
            className={`flex items-center justify-center w-9 h-9 rounded-full ${
              sortType === "alphabetical"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
            title="Ordenar A-Z"
          >
            <span className="sr-only">Alphabetical sort</span>
            <BiSortAZ size={18} />
          </button>
          <button
            onClick={() => setSortType("availability")}
            className={`flex items-center justify-center w-9 h-9 rounded-full ${
              sortType === "availability"
                ? "bg-blue-500 text-white"
                : "bg-gray-200 hover:bg-gray-300"
            }`}
            title="Ordenar por disponibilidad"
          >
            <span className="sr-only">Sort by activity</span>
            <MdOutlineEventAvailable size={18} />
          </button>
        </div>
      </div>

      {/* Agent List */}

      <div className="grid grid-cols-2 gap-2 overflow-y-auto custom-scroll">
        {filteredAndSortedAgents.map((agent) => {
          const {
            name,

            email,
            photo,
            presence,
            presenceActivity,
            statusStartTime,
            number,
            queue,
          } = agent;

          return (
            <div
              key={email}
              className={`bg-primary-light dark:bg-primary-dark rounded-lg shadow-md p-2 flex items-center justify-between space-x-4 transition-colors duration-300`}
            >
              {/* Información del agente */}
              <div className="flex items-center space-x-4">
                <FluentProvider
                  theme={isDarkMode ? webDarkTheme : webLightTheme}
                >
                  <Persona
                    name={name}
                    secondaryText={
                      loading ? (
                        <div className="w-32 h-3 bg-gray-300 rounded animate-pulse mt-1"></div>
                      ) : presenceActivity ? (
                        <AgentStatus
                          status={capitalize(presenceActivity)}
                          statusStartTime={statusStartTime}
                          number={number}
                          queue={queue}
                        ></AgentStatus>
                      ) : null
                    }
                    presence={{
                      status:
                        presence === "BeRightBack"
                          ? "Away"
                          : presence === "PresenceUnknown"
                          ? "Offline"
                          : presence || "undefined",
                    }}
                    avatar={photo ? { image: { src: photo } } : undefined}
                    style={{
                      backgroundColor: isDarkMode ? "#1a202c" : "white",
                      color: isDarkMode ? "#1a202c" : "white",
                    }}
                  />
                </FluentProvider>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RealtimeAgents;
